import { next } from '@ember/runloop';
import EmberService, { service } from '@ember/service';

import moment from 'moment';

import type { APIData, Service } from 'webapp-types';
import type { PromotionIdentifier } from 'webapp/enums/enums';
import {
	BillingAddonType,
	BillingCycle,
	BillingFeature,
	PlanType,
	PopupEvent,
} from 'webapp/enums/enums';
import { TourId } from 'webapp/enums/tour';
import { TrackingEvent } from 'webapp/enums/tracking';
import { UserSettingsKey } from 'webapp/enums/user-settings';

import BillingPopupAddonTrialComponent from 'webapp/components/billing/popup-addon-trial';
import BillingPopupPlanUpgradeTrialComponent from 'webapp/components/billing/popup-plan-upgrade-trial';
import BillingPromotionsGrowth2024PopupDiscountEndingComponent from 'webapp/components/billing/promotions/growth-2024/popup-discount-ending';
import BillingPromotionsGrowth2024PopupDiscountUseCasesComponent from 'webapp/components/billing/promotions/growth-2024/popup-discount-use-cases';
import BillingPromotionsGrowth2024PopupTrialEndedComponent from 'webapp/components/billing/promotions/growth-2024/popup-trial-ended';
import BillingPromotionsGrowth2024PopupTrialStartedComponent from 'webapp/components/billing/promotions/growth-2024/popup-trial-started';

import type { Tour } from 'webapp/models-custom';
import type BillingPlan from 'webapp/models-custom/billing-plan';
import type ExpiredPromotion from 'webapp/models-custom/expired-promotion';
import { BoardFilterId } from 'webapp/models-custom/filter-sort/board';
import { BoardLoopFilterId } from 'webapp/models-custom/filter-sort/board-loop';
import type PopupModal from 'webapp/models-custom/popup/modal';
import type Promotion from 'webapp/models-custom/promotion';
import type { PromotionsData } from 'webapp/models-custom/promotions';
import Promotions from 'webapp/models-custom/promotions';

import type {
	TrackingOriginForSubscriptionChangePopup,
	TrackingOriginTypeForTrialPopupOpened,
} from 'webapp/services/billing';

/* Promotions of plans / addons
	Show *Popup* and / or *Banner* - on Event [ Load, Action ] or Always (ex: Banner) - (from - to) show *on frequency / condition*

	1. Growth plan promo (May 2024) - https://docs.google.com/document/d/19Zd68y1gKQHF6vciW4-vphLXzTIeM0QNPHW3uz-4Yi0/edit
		Day 0 - PromotionIdentifier.TRIAL_GROWTH_FEATURES is activated and ends in 15 days, PromotionIdentifier.GROWTH_PROMOTION is enabled and ends in min 30days (end of year for some plans)

		Popups:
			1. trial activated info - on Load - (0 - PromotionIdentifier.TRIAL_GROWTH_FEATURES activeUntil day) show only once first time
			2. growth use cases - on Load - (last 7 days before PromotionIdentifier.TRIAL_GROWTH_FEATURES activeUntil day) show only once first time unless 1 is shown (or if it shown today, show me tomorrow) (Initially imagined as every time user does actions on growth features (example, saves bencmark))
			3. trial ended, promo still active - on Load - (PromotionIdentifier.TRIAL_GROWTH_FEATURES activeUntil day - PromotionIdentifier.GROWTH_PROMOTION activeUntil day) show only once first time
			4. promo ending today - on Load - (PromotionIdentifier.GROWTH_PROMOTION activeUntil day) show only once first time

		Banners:
			1. trial activated info - Always - (0 - PromotionIdentifier.TRIAL_GROWTH_FEATURES activeUntil day)
			2. growth plan promo - Always - (PromotionIdentifier.TRIAL_GROWTH_FEATURES activeUntil day - PromotionIdentifier.GROWTH_PROMOTION activeUntil day), unless snoozed

	2. Trial higher plan
		Which plan types are offered a trial is governed by the backend via each promotion's productIds (matched client-side by Promotion.isForPlanType) - it is no longer hardcoded on the client.
		Historically this was growth and professional (paying) plans, but not premium (because it has growth features and ONLY advanced security features extra); the example scenarios below reflect that original rollout.
		Example scenarios:
			1. If user is on starter, and encounters a professional feature, show popup to offer a trial for professional
			2. If user is on starter or professional, and encounters a growth feature, show popup to offer a trial for growth
			3. If user is on starter, and has a paying trial for professional enabled (Scenario 1), and encounters a growth feature, show popup to offer a trial for growth

*/

const PromotionPopupTrackingType = {
	TRIAL_STARTED: 'trial_started',
	TRIAL_ENDED: 'trial_ended',
	DISCOUNT_GENERAL: 'discount_general',
	DISCOUNT_ENDING: 'discount_ending',
} as const;
export type TrackingTypeForPromotionPopupShown =
	(typeof PromotionPopupTrackingType)[keyof typeof PromotionPopupTrackingType];

interface OnPromoInfoPopupOpenData {
	userSettingsKey: UserSettingsKey;
	tracking: {
		event: TrackingEvent;
		type: TrackingTypeForPromotionPopupShown;
		planName: PlanType;
	};
}

export default class FlowPromotionsService extends EmberService {
	@service('request') declare requestService: Service.Request;
	@service('popup') declare popupService: Service.Popup;
	@service('billing') declare billingService: Service.Billing;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('account') declare accountService: Service.Account;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('timer') declare timerService: Service.Timer;
	@service('tour') declare tourService: Service.Tour;

	promotions: Promotions; // updated via updatePromotions

	constructor() {
		// eslint-disable-next-line prefer-rest-params
		super(...arguments);
		this.promotions = new Promotions();
	}

	get showPromotions() {
		return (
			!this.currentUserService.isViewer &&
			// !this.currentUserService.hasDataboxEmail &&
			!this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins &&
			(!this.accountService.isClient || this.currentUserService.isAgencyUserInClientSpace)
		);
	}

	get hasAnyPlanActiveAutomatedTrial() {
		return this.promotions.hasAnyHigherPlanAutomatedTrialActivePromotions;
	}

	get activePlanPromotion() {
		return (
			this.promotions.higherPlanAutomatedTrialActivePromotions[0] ||
			this.promotions.higherPlanAutomatedTrialOfferActivePromotions[0]
		);
	}

	get expiredPromotions(): ExpiredPromotion[] {
		return this.promotions.expired;
	}

	getAvailableTrialPromotion(plan: BillingPlan): Promotion | undefined {
		return this.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(plan.planType)[0];
	}

	getActiveTrialAddonPromotion(productId: string): Promotion | undefined {
		return this.promotions.trialAddonActivePromotions.find((promotion) =>
			promotion.productIds?.includes(productId),
		);
	}

	getOptionalTrialAddonPromotion(productId: string): Promotion | undefined {
		return this.promotions.trialAddonOptionPromotions.find((promotion) =>
			promotion.productIds?.includes(productId),
		);
	}

	// set fakeReplyGrowth2024.options[0].activeUntil to moment().add(2, 'hours').unix() to invoke DiscountEndingPopup
	// fakeReplyGrowth2024 = `{"active":{"id":"trial_growth_features","type":"trialing_features","name":"Trial Growth Features 14 Days, Free!","description":"Improve your company's performance with Forecasts, AI-performance summaries, and more.","billingDescriptions":null,"productIds":["growth"],"activeUntil":1717856032,"discount":null},"options":[{"id":"growth_promotion","type":"discount","name":"Growth Promotions 2024","description":"Upgrade now and get 100$ off for three months","billingDescriptions":{"monthly":"Upgrade to Growth by June 15th and get $100 monthly discount for the first 6 months","quarterly":"Upgrade to Growth by June 15th and get $300 quarterly discount for the first 2 quarters.","yearly":"Upgrade to Growth by June 15th and get $600 off for the first year."},"productIds":["growth"],"activeUntil":1718028865,"discount":{"id":null,"name":"Growth Promo 2024","amount":10000,"productId":null,"type":"amount_off","durationType":"repeating","identifier":"growth_promo","durationInMonths":3}}]}`;

	requestPromotions(): Promise<APIData.Billing.Promotions.Response> {
		const promise = this.requestService.api<APIData.Billing.Promotions.Response>(
			'GET',
			'/account/promotions',
		);

		void promise.then((reply) => {
			// reply = JSON.parse(this.fakeReplyGrowth2024);

			this.updatePromotions(reply);
		});

		return promise.catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		) as Promise<APIData.Billing.Promotions.Response>;
	}

	updatePromotion(promotion: {
		promotionId: PromotionIdentifier;
		commitment: boolean;
	}): Promise<APIData.Billing.Promotions.Response> {
		const promise = this.requestService.api<APIData.Billing.Promotions.Response>(
			'PUT',
			'/account/promotions',
			promotion,
		);

		void promise.then((reply) => {
			this.updatePromotions(reply);
		});

		return promise.catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		) as Promise<APIData.Billing.Promotions.Response>;
	}

	startPromotion(promotion: {
		promotionId: PromotionIdentifier;
		productId: BillingFeature;
	}): Promise<APIData.Billing.Promotions.Response> {
		const promise = this.requestService.api<APIData.Billing.Promotions.Response>(
			'POST',
			'/account/promotions',
			promotion,
		);

		void promise.then((reply) => {
			this.updatePromotions(reply);
		});

		return promise.catch(
			this.requestService.showAllValidationErrors.bind(this.requestService),
		) as Promise<APIData.Billing.Promotions.Response>;
	}

	updatePromotions(data: PromotionsData): void {
		if (data.active) {
			data.active = data.active;
		} else {
			data.active = [];
		}

		this.promotions.updatePromotionsData(data);
	}

	// --- Growth plan promo (May 2024)

	get hasSeenGrowth2024PopupTrialStarted(): boolean {
		return !!this.userSettingsService.userSettings[
			UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_TRIAL_STARTED_SEEN_TIMESTAMP
		];
	}

	get hasSeenGrowth2024PopupTrialEnded(): boolean {
		return !!this.userSettingsService.userSettings[
			UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_TRIAL_ENDED_SEEN
		];
	}

	get hasSeenGrowth2024PopupDiscountEnding(): boolean {
		return !!this.userSettingsService.userSettings[
			UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_DISCOUNT_ENDING_SEEN
		];
	}

	get hasSeenGrowth2024PopupDiscountUseCases(): boolean {
		return !!this.userSettingsService.userSettings[
			UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_DISCOUNT_USE_CASES_SEEN
		];
	}

	get isGrowth2024BannerDiscountSnoozed(): boolean {
		const snoozeTimestamp = this.userSettingsService.userSettings[
			UserSettingsKey.PROMOTION_GROWTH_2024_BANNER_DISCOUNT_SNOOZE_TIMESTAMP
		] as number | undefined;
		return !!snoozeTimestamp && snoozeTimestamp > this.timerService.currentTs;
	}

	openPromoInfoPopup(popup: PopupModal, onPromoInfoPopupOpenData: OnPromoInfoPopupOpenData) {
		popup.on(PopupEvent.OPENED, this, () => {
			next(() => {
				const value =
					onPromoInfoPopupOpenData.userSettingsKey ===
					UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_TRIAL_STARTED_SEEN_TIMESTAMP
						? this.timerService.currentTs
						: true;

				void this.userSettingsService.save(onPromoInfoPopupOpenData.userSettingsKey, value);
			});

			this.analyticsService.event(onPromoInfoPopupOpenData.tracking.event, {
				type: onPromoInfoPopupOpenData.tracking.type,
				plan_name: onPromoInfoPopupOpenData.tracking.planName,
			});
		});

		popup.open();
	}

	checkToOpenGrowth2024Popup(): void {
		// Check top of document for flow description
		if (this.popupService.anyPopupsOpen || !this.showPromotions) {
			return;
		}

		if (!this.checkToOpenGrowth2024TrialStartedPopup()) {
			if (!this.checkToOpenGrowth2024TrialEndedPopup()) {
				if (!this.checkToOpenGrowth2024DiscountEndingPopup()) {
					this.checkToOpenGrowth2024DiscountUseCasesPopup();
				}
			}
		}
	}

	checkToOpenGrowth2024TrialStartedPopup() {
		let willOpenPopup = false;

		if (this.hasSeenGrowth2024PopupTrialStarted) {
			return willOpenPopup;
		}

		const promotion = this.promotions.growthFeatureTrialActivePromotion;

		willOpenPopup = !!promotion?.isCurrent;

		if (willOpenPopup) {
			const popup = this.popupService.createModal(
				BillingPromotionsGrowth2024PopupTrialStartedComponent,
				{
					popupClass:
						'popup-type-billing-promotions-common popup-type-billing-promotions-growth-2024-trial-started',
				},
			);

			this.openPromoInfoPopup(popup, {
				userSettingsKey:
					UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_TRIAL_STARTED_SEEN_TIMESTAMP,
				tracking: {
					event: TrackingEvent.PROMOTE_PLAN_POPUP_SHOWN,
					type: PromotionPopupTrackingType.TRIAL_STARTED,
					planName: PlanType.GROWTH,
				},
			});
		}

		return willOpenPopup;
	}

	checkToOpenGrowth2024TrialEndedPopup() {
		let willOpenPopup = false;

		if (this.hasSeenGrowth2024PopupTrialEnded) {
			return willOpenPopup;
		}

		const featureTrialActivePromotion = this.promotions.growthFeatureTrialActivePromotion,
			planDiscountOptionPromotion = this.promotions.growthPlanDiscountOptionPromotion;

		willOpenPopup = !featureTrialActivePromotion && !!planDiscountOptionPromotion; // trialEndedAndPromoActive

		if (willOpenPopup) {
			const popup = this.popupService.createModal(
				BillingPromotionsGrowth2024PopupTrialEndedComponent,
				{
					popupClass:
						'popup-type-billing-promotions-common popup-type-billing-promotions-growth-2024-trial-ended',
				},
			);

			this.openPromoInfoPopup(popup, {
				userSettingsKey: UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_TRIAL_ENDED_SEEN,
				tracking: {
					event: TrackingEvent.PROMOTE_PLAN_POPUP_SHOWN,
					type: PromotionPopupTrackingType.TRIAL_ENDED,
					planName: PlanType.GROWTH,
				},
			});
		}

		return willOpenPopup;
	}

	checkToOpenGrowth2024DiscountEndingPopup() {
		let willOpenPopup = false;

		if (this.hasSeenGrowth2024PopupDiscountEnding) {
			return willOpenPopup;
		}

		const promotion = this.promotions.growthPlanDiscountOptionPromotion,
			currentDate = this.timerService.currentTs;

		willOpenPopup =
			!!promotion?.isCurrent &&
			!!promotion.activeUntil &&
			currentDate > moment.unix(promotion.activeUntil).subtract(1, 'days').unix();

		if (willOpenPopup) {
			const popup = this.popupService.createModal(
				BillingPromotionsGrowth2024PopupDiscountEndingComponent,
				{
					popupClass:
						'popup-type-billing-promotions-common popup-type-billing-promotions-growth-2024-discount-ending',
				},
			);

			this.openPromoInfoPopup(popup, {
				userSettingsKey: UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_DISCOUNT_ENDING_SEEN,
				tracking: {
					event: TrackingEvent.PROMOTE_PLAN_POPUP_SHOWN,
					type: PromotionPopupTrackingType.DISCOUNT_ENDING,
					planName: PlanType.GROWTH,
				},
			});
		}

		return willOpenPopup;
	}

	checkToOpenGrowth2024DiscountUseCasesPopup() {
		let willOpenPopup = false;

		if (this.hasSeenGrowth2024PopupDiscountUseCases) {
			return willOpenPopup;
		}

		const promotion = this.promotions.growthFeatureTrialActivePromotion;

		willOpenPopup = !!promotion?.isCurrent && promotion.daysRemaining < 7;

		if (willOpenPopup) {
			const popup = this.popupService.createModal(
				BillingPromotionsGrowth2024PopupDiscountUseCasesComponent,
				{
					popupClass:
						'popup-type-billing-promotions-common popup-type-billing-promotions-growth-2024-discount-use-cases',
				},
			);
			this.openPromoInfoPopup(popup, {
				userSettingsKey:
					UserSettingsKey.PROMOTION_GROWTH_2024_POPUP_DISCOUNT_USE_CASES_SEEN,
				tracking: {
					event: TrackingEvent.PROMOTE_PLAN_POPUP_SHOWN,
					type: PromotionPopupTrackingType.DISCOUNT_GENERAL,
					planName: PlanType.GROWTH,
				},
			});
		}

		return willOpenPopup;
	}

	openGrowthPromoUpgradePopup(origin: TrackingOriginForSubscriptionChangePopup) {
		const targetPlan = this.getPlanByTypeForCurrentCycle(PlanType.GROWTH);

		if (targetPlan) {
			this.billingService.switchPlan(targetPlan, {
				billingPeriodChangePopup: origin,
			});
		}
	}

	// --- Growth plan promo (May 2024)

	getPlanByTypeForCurrentCycle(planType: PlanType) {
		const currentPlan = this.billingService.activePlan,
			targetPlan = this.billingService.availablePlans
				.filter((item) => item.planType === planType)
				.find(
					(item) =>
						item.billingCycle === (currentPlan?.billingCycle ?? BillingCycle.MONTHLY),
				);

		return targetPlan;
	}

	openStartAddonTrialPopup(feature?: BillingFeature) {
		const popup = this.popupService.createModal(BillingPopupAddonTrialComponent, {
			popupClass: 'popup-type-billing-plan-upgrade-common popup-type-billing-addon-trial',
		});

		popup.open({
			feature: feature!,
		});
	}

	// --- Trial addon

	getTrialAddonPromotions() {
		return this.promotions.trialAddonOptionPromotions;
	}

	getBillingAddonTypeForFeature(feature: BillingFeature): BillingAddonType | null {
		switch (feature) {
			case BillingFeature.OKR:
				return BillingAddonType.OKR;
			default:
				return null;
		}
	}

	// --- Trial addon

	// --- Trial higher plan

	getHigherPlanAutomatedTrialOptionPromotionsForPlanType(planType: PlanType) {
		return this.promotions.getHigherPlanAutomatedTrialOptionPromotionsForPlanType(planType);
	}

	openStartHigherPlanAutomatedTrialPopup(
		targetPlan: BillingPlan,
		feature?: BillingFeature,
		trackingOrigin?: TrackingOriginTypeForTrialPopupOpened | null,
		useOriginForTrackingFeature?: boolean,
	) {
		const popup = this.popupService.createModal(BillingPopupPlanUpgradeTrialComponent, {
			popupClass:
				'popup-type-billing-plan-upgrade-common popup-type-billing-plan-upgrade-trial',
		});

		popup.open({
			targetPlan,
			feature,
			trackingOrigin,
			useOriginForTrackingFeature,
		});
	}

	// --- Trial higher plan

	// --- Recently deleted promo tour
	recentlyDeletedTour: Tour | null = null;

	startRecentlyDeletedTourForBoards() {
		this.startRecentlyDeletedTour(
			$('#page-boards').find(
				`.filterSwitcher.f${BoardFilterId.CATEGORY} ui-icon[name="ChevronDown"]`,
			),
			'board',
		);
	}

	startRecentlyDeletedTourForBoardLoops() {
		this.startRecentlyDeletedTour(
			$('#page-board-loops').find(
				`.filterSwitcher.f${BoardLoopFilterId.CATEGORY} ui-icon[name="ChevronDown"]`,
			),
			'board-loop',
		);
	}

	private startRecentlyDeletedTour(el: JQuery<HTMLElement>, type: 'board' | 'board-loop') {
		if (!this.tourService.isEligibleForRecentlyDeletedTour) {
			return;
		}

		this.saveRecentlyDeletedPromoTimestamp();

		const timestampNow = moment().unix();
		if (timestampNow < this.userSettingsService.recentlyDeletedTourTimestamp) {
			setTimeout(() => {
				const tour = this.tourService.initTour(TourId.RECENTLY_DELETED, {
					el,
					extraClass: `popup-type-blue-bubble--recently-deleted-${type}`,
				});

				if (tour) {
					this.recentlyDeletedTour = tour;
					tour.start();
				}
			}, 500);
		}
	}

	endRecentlyDeletedTour() {
		this.recentlyDeletedTour?.hideBubble();
		this.recentlyDeletedTour = null;
	}

	private saveRecentlyDeletedPromoTimestamp() {
		if (!this.userSettingsService.recentlyDeletedTourTimestamp) {
			void this.userSettingsService.save(
				UserSettingsKey.PROMOTION_RECENTLY_DELETED_TIMESTAMP,
				moment().add(30, 'days').unix(),
			);
		}
	}

	// --- Recently deleted promo tour
}

declare module '@ember/service' {
	interface Registry {
		'flow/promotions': FlowPromotionsService;
	}
}
